import type { intelligence } from '@cwos/api-client';

/**
 * explanationView.ts — pure view logic for the §24 explanation envelope.
 *
 * Everything here is a function of its arguments, exactly like `turnView.ts`
 * beside it, so the honesty rules below can be tested without a DOM and so the
 * screen never re-derives any of them inline. A second spelling of "the check
 * refused this" is a second spelling that can drift into politeness.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE THREE ARMS, AND WHY THE MIDDLE ONE IS THE WHOLE POINT.
 *
 * `TurnResponse.explanation` is a THREE-state field and the server composes it
 * that way on purpose (`intelligence/explain/composer.ts`, lane B7a):
 *
 *   ABSENT     the answer carried no explanation block. The ORDINARY case, and
 *              the honest one for a figure, a list or a greeting. It renders as
 *              NOTHING. Collapsing it into the refusal arm would put a warning
 *              on every metric answer in the product — "we did not look",
 *              wearing the name of "the model failed the guard".
 *
 *   REJECTED   `{ok:false, code}`. The model DID write an explanation and the
 *              server refused it against a named rule. This is the arm a
 *              surface skips, because it looks like the absent one from a
 *              distance — the panel is empty either way. It is not the same
 *              fact. A reader must be able to tell an answer whose reasoning
 *              FAILED A GUARD from one that never claimed reasoning at all,
 *              and only this arm's sentence says which.
 *
 *   COMPOSED   `{ok:true, explanation}`. Every claim in it is pinned to a
 *              retrieval this turn actually made, and the affected scope's org
 *              is the server's own rather than the model's.
 *
 * THE REFUSED CONTENT IS NOT ON THE WIRE, ON PURPOSE. `ExplanationRejection`
 * carries `message` and `factorsLackingMechanism` server-side, and both restate
 * the model's own factor LABELS — free text written about this tenant's
 * operation. The route publishes the `code` and nothing else (routes.ts, "A
 * REFUSAL RIDES AS ITS CODE AND NOTHING ELSE"), so this file cannot show the
 * reader what was refused even if it wanted to. What it CAN say, and does, is
 * which rule refused it and what that means for the answer above.
 */

type Explanation = intelligence.Explanation;
type ExplanationVerdict = intelligence.ExplanationVerdict;
type ExplanationRejectionCode = intelligence.ExplanationRejectionCode;
type ApplicablePolicy = intelligence.ApplicablePolicy;
type ExplanationTemporal = intelligence.ExplanationTemporal;
type ExplanationOwner = intelligence.ExplanationOwner;
type PolicySourceElement = ApplicablePolicy['elements'][number];
type ExplanationFactor = Explanation['likelyFactors'][number];

// ---------------------------------------------------------------------------
// The verdict, as three states a renderer can switch on
// ---------------------------------------------------------------------------

export type ExplanationState =
  /** No block. Renders nothing. NOT a warning, NOT an error, NOT a refusal. */
  | { readonly kind: 'absent' }
  /** A block was written and a named guard refused it. Renders a sentence. */
  | {
      readonly kind: 'rejected';
      readonly code: ExplanationRejectionCode;
      readonly text: string;
    }
  /** A block was written and every claim in it resolved. Renders in full. */
  | { readonly kind: 'composed'; readonly explanation: Explanation };

/**
 * What each refusal code means to the person holding the answer.
 *
 * Written to the reader, not to an operator: the code names a rule application
 * and a reader does not know the rules, so each sentence says what the check
 * was looking for and what it did NOT find. Every one of them ends by saying
 * the answer above is unchanged, because that is the question a reader has the
 * moment they see the word "refused" — and the answer's own trustworthiness is
 * reported separately and unaffected, by `groundingCaution`.
 *
 * NONE OF THEM SAYS "TRY AGAIN". A refusal here is a property of the block the
 * model wrote this time; asking again may or may not produce a different one,
 * and inviting a retry would frame a working guard as a transient fault — the
 * mistake `answerLimits` had to have corrected out of it for the privacy floor.
 */
const REJECTION_TEXT: Readonly<Record<ExplanationRejectionCode, string>> = {
  invalid_envelope:
    'Phibian wrote out its reasoning for this answer and the check refused it: the reasoning wasn’t in a shape the check could read, so none of it could be verified. The answer above is unchanged — what’s missing is the account of how it was reached.',
  unsupported_evidence_ref:
    'Phibian wrote out its reasoning for this answer and the check refused it: the reasoning pointed at records this turn did not actually look at. The answer above is unchanged, and how far the answer itself is backed by your records is reported separately — this is about the account of how it was reached, which could not be trusted.',
  causation_without_mechanism:
    'Phibian wrote out its reasoning for this answer and the check refused it: it said one thing caused another without naming anything that connects them — a written rule, a threshold that was crossed, a scheduled change, a failure, or something somebody did. The answer above is unchanged. Things moving together is not the same as one causing the other, and the check will not let the stronger claim through on the weaker evidence.',
};

/**
 * The single sentence that separates this arm from the absent one.
 *
 * A code this bundle does not know still gets a refusal sentence rather than
 * silence. An unknown member of a closed vocabulary means the server refused
 * for a reason shipped after this bundle was built, and the one thing a surface
 * must not do with that is fall back to rendering nothing — which is the
 * spelling of ABSENT, the exact collapse this module exists to prevent.
 */
export function rejectionText(code: ExplanationRejectionCode): string {
  return (
    REJECTION_TEXT[code] ??
    'Phibian wrote out its reasoning for this answer and a check refused it, so the account of how the answer was reached isn’t shown. The answer above is unchanged.'
  );
}

export function explanationState(verdict: ExplanationVerdict | undefined): ExplanationState {
  // ABSENT IS THE FIELD BEING MISSING, and nothing else. Not an empty object,
  // not a composed explanation with no factors — those are different facts that
  // reach the other two arms.
  if (verdict === undefined) return { kind: 'absent' };
  if (verdict.ok) return { kind: 'composed', explanation: verdict.explanation };
  return { kind: 'rejected', code: verdict.code, text: rejectionText(verdict.code) };
}

// ---------------------------------------------------------------------------
// §24 APPLICABLE POLICY — three states, and a date on all of them
// ---------------------------------------------------------------------------

/**
 * What the doctrine corpus was asked and what it said.
 *
 * THREE STATES. The element being ABSENT and the element being PRESENT WITH NO
 * ELEMENTS are different answers, and `explanationSchema` keeps them different
 * with an optional field for exactly this reason: "no policy governs you" in
 * front of a reader for whom nothing was ever looked up is a false statement
 * about their compliance posture.
 *
 * AND THE DATE RIDES ON BOTH OF THE LATTER TWO. B7e added `asOf` because the
 * element without it is a citation with no tense: "two hours of supervision,
 * v3, effective 2026-01-01" is a true statement about a rule and says nothing
 * about whether it is the rule that governed the June the question was about.
 * "The corpus covers no such topic" and "…as of June" are different answers,
 * and only the date tells them apart.
 */
export type PolicyResolution =
  /** No doctrine was resolved this turn. The corpus was never asked. */
  | { readonly kind: 'not_consulted' }
  /** The corpus WAS asked, on a subject it covers, and nothing applied. */
  | {
      readonly kind: 'none_applied';
      readonly asOf: string | null;
      readonly subjects: readonly string[];
    }
  /** Rules apply, as they stood on `asOf`. */
  | {
      readonly kind: 'applies';
      readonly asOf: string | null;
      readonly elements: readonly PolicySourceElement[];
      readonly subjects: readonly string[];
      readonly constrainedByExternal: boolean;
      readonly unresolvedContradictions: ApplicablePolicy['unresolvedContradictions'];
      readonly reportableConflicts: ApplicablePolicy['reportableConflicts'];
    };

export function policyResolution(policy: ApplicablePolicy | undefined): PolicyResolution {
  if (policy === undefined) return { kind: 'not_consulted' };
  if (policy.elements.length === 0) {
    return { kind: 'none_applied', asOf: policy.asOf, subjects: policy.subjects };
  }
  return {
    kind: 'applies',
    asOf: policy.asOf,
    elements: policy.elements,
    subjects: policy.subjects,
    constrainedByExternal: policy.constrainedByExternal,
    unresolvedContradictions: policy.unresolvedContradictions,
    reportableConflicts: policy.reportableConflicts,
  };
}

/**
 * A UTC calendar date as a person reads it, or null when it is not one.
 *
 * Deliberately parsed strictly rather than handed to `new Date(string)`: the
 * field is a calendar DATE (`YYYY-MM-DD`), and letting a Date constructor
 * interpret an arbitrary string would silently apply the viewer's timezone and
 * shift the boundary by a day — the exact class of defect the house rules call
 * out ("a UTC week anchor for an America/Chicago org is wrong by 416 hours a
 * year"). `Date.UTC` on the three integers cannot drift.
 */
export function readableDate(iso: string | null): string | null {
  if (iso === null) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match === null) return null;
  const [, y, m, d] = match;
  const ms = Date.UTC(Number(y), Number(m) - 1, Number(d));
  if (Number.isNaN(ms)) return null;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(ms));
}

/**
 * The sentence a policy resolution earns, or null when there is nothing to say.
 *
 * `not_consulted` is the null: a surface that announced "no policy was checked"
 * on every ordinary answer would be `absent`-as-`rejected` again, one element
 * down.
 *
 * The `asOf: null` wording is NOT "as of an unknown date" — that would read as
 * a missing field. It is the composer telling us that every candidate was
 * rejected for `invalid_context_effective_date`, so the null EXPLAINS the
 * emptiness rather than qualifying it, and the sentence says so.
 */
export function policySentence(resolution: PolicyResolution): string | null {
  if (resolution.kind === 'not_consulted') return null;
  const on = readableDate(resolution.asOf);
  if (resolution.kind === 'none_applied') {
    return on === null
      ? 'Phibian checked your organization’s policy on this and could not settle which date to check it for, so no rule could be selected as being in force. This is not a finding that nothing governs you — it is a question that could not be dated.'
      : `Phibian checked your organization’s policy on this as it stood on ${on}, and no rule applied. That is a checked answer about a topic the corpus covers, not an absence of policy.`;
  }
  return on === null
    ? 'These are the rules Phibian found applicable, but the date they were checked against could not be settled — so whether they are the rules that were in force at the time this answer is about has not been established.'
    : `These are the rules that were in force on ${on} — the date Phibian narrowed the corpus to, not today’s date.`;
}

/** The role a rule plays, in words that keep "requires" and "suggests" apart. */
const ROLE_LABEL: Readonly<Record<PolicySourceElement['role'], string>> = {
  governing: 'Governs this',
  external_floor: 'Required from outside',
  advisory: 'Advisory',
  superseded_by_more_specific: 'Superseded by a more specific rule',
  doctrine_default: 'Default, where no local rule exists',
};

export function roleLabel(role: PolicySourceElement['role']): string {
  return ROLE_LABEL[role] ?? role;
}

/**
 * Where a rule came from — WITHHELD, ABSENT and NAMED kept apart.
 *
 * `sourceWithheld` is notation (B) of `docs/withholding-contract.md`: the
 * source exists and this reader may not be told it. Rendering that as
 * "unsourced" would send a compliance reviewer to fix a corpus that is not
 * broken, which is the whole reason the flag is a field rather than an
 * omission.
 */
export type PolicySourceLine =
  | { readonly kind: 'withheld'; readonly text: string }
  | { readonly kind: 'absent'; readonly text: string }
  | {
      readonly kind: 'named';
      readonly title: string;
      readonly detail: string | null;
      readonly sourceId: string;
      /** http/https only. See {@link safeHttpUrl}. */
      readonly referenceUrl: string | null;
    };

export function policySourceLine(element: PolicySourceElement): PolicySourceLine {
  if (element.sourceWithheld) {
    return {
      kind: 'withheld',
      text: 'Where this rule comes from isn’t shown to you. It has a source — you may not be told which.',
    };
  }
  if (element.source === null) {
    return { kind: 'absent', text: 'No source is recorded for this rule.' };
  }
  const source = element.source;
  const parts = [source.publisher, source.citation, source.version].filter(
    (part): part is string => typeof part === 'string' && part.trim() !== '',
  );
  return {
    kind: 'named',
    title: source.title,
    detail: parts.length > 0 ? parts.join(' · ') : null,
    sourceId: source.sourceId,
    referenceUrl: safeHttpUrl(source.referenceUrl),
  };
}

/** Who made a rule authoritative here — withheld, absent and named kept apart. */
export function approvalLine(element: PolicySourceElement): string | null {
  if (element.approvalWithheld) {
    return 'Who approved this here isn’t shown to you.';
  }
  if (element.approval === null) {
    // NOT a defect and NOT a gap. External library material has no in-org
    // approver by construction; saying "unapproved" about a statute would be
    // wrong in the direction that gets somebody to go and approve one.
    return 'This has no approver in your organization — it comes from outside it.';
  }
  const on = readableDate(element.approval.approvedAt);
  return on === null ? 'Approved in your organization.' : `Approved in your organization on ${on}.`;
}

/**
 * A URL from an untrusted source, or null.
 *
 * `referenceUrl` reaches this bundle out of the doctrine corpus, which is fed
 * by tenant uploads and external ingestion — attacker-controllable text in the
 * founder's fifth ruling's exact sense, where prompt-injection and content
 * defences apply EVEN AFTER HUMAN APPROVAL. React escapes a string it renders
 * as TEXT; it does not stop `javascript:` in an `href`, which is the one field
 * on this whole surface that becomes behaviour rather than characters.
 *
 * ALLOW-LIST, NOT DENY-LIST. `javascript:` has enough spellings (`java\tscript:`,
 * uppercase, entity-encoded) that a blocklist is a losing game; two schemes are
 * admitted and everything else — `data:`, `blob:`, `vbscript:`, a relative path
 * that would resolve inside the admin app — is refused.
 */
export function safeHttpUrl(url: string | undefined): string | null {
  if (typeof url !== 'string' || url.trim() === '') return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
}

// ---------------------------------------------------------------------------
// §24 LIKELY FACTORS — three bases, and rendering them alike undoes them
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// §24 EVIDENCE — which retrieval backs which sentence
// ---------------------------------------------------------------------------

/**
 * ONE CITATION MARKER: the number of the manifest line a claim rests on.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHAT WAS MISSING, AND WHY IT WAS THE WHOLE ELEMENT.
 *
 * The server derives `supportingEvidence` and a per-delta / per-factor
 * `evidenceRef` entirely on its own, and guards them hard: a ref that does not
 * resolve to a retrieval THIS turn actually made — or resolves to one that
 * accessed nothing — rejects the entire envelope. Nothing rendered any of it.
 * `supportingEvidence` and `evidenceRef` appeared zero times across the whole
 * admin bundle, and the evidence panel beside the explanation described the
 * turn's provenance as prose with no sentence attached to any of it. A
 * per-claim citation that no surface draws is a per-claim citation that does
 * not exist: the reader saw a claim, saw a list, and had to take on trust that
 * the two were related.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * A NUMBER, NEVER THE `ev_…` ID.
 *
 * The refs are server-issued verification tokens and they are exactly the class
 * of string `evidenceView.ts` exists to keep off a screen — beside the content
 * hash and the scope UUIDs, which it already refuses to render. The number is
 * the same pointer with none of the payload, and it is the manifest POSITION,
 * so the marker and the line it points at are one ordering rather than two.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * `resolved: false` IS A STATE, NOT A HOLE.
 *
 * A composed envelope cannot legally carry a ref its own turn's manifest does
 * not hold. If one arrives here anyway, the explanation and the manifest in the
 * same response disagree, and that is an integrity fact a reader is owed —
 * exactly the reasoning that keeps `rejected` visibly apart from `absent` one
 * file over. So it renders as a marker that says the source is not in this
 * turn's record, and never as a silently omitted citation, which would show the
 * reader an uncited claim as if it were an unciting one.
 */
export interface Citation {
  /** The server-issued ref. Carried for keys and titles, never rendered. */
  readonly ref: string;
  /** What the reader sees inside the brackets: a number, or the unresolved mark. */
  readonly label: string;
  /** The `title`/`aria` sentence, so the marker is not a bare glyph. */
  readonly description: string;
  readonly resolved: boolean;
}

/** The mark for a ref this turn's manifest does not hold. Deliberately visible. */
const UNRESOLVED_CITATION = '?';

/**
 * The citations for one claim, in the order the server wrote them, de-duplicated.
 *
 * De-duplicated because a claim resting twice on the same retrieval rests on it
 * once; rendering "[1] [1]" would invite a reader to count citations as if the
 * count meant something, which is the same defect the evidence summary's
 * removed "— 2 retrievals" was.
 *
 * Total: a non-array, a non-string member and an empty list all yield an empty
 * result, so a malformed envelope loses its markers and never the answer.
 */
export function citationsFor(
  refs: readonly string[] | undefined,
  index: ReadonlyMap<string, number>,
): Citation[] {
  if (!Array.isArray(refs)) return [];
  const seen = new Set<string>();
  const out: Citation[] = [];
  for (const ref of refs) {
    if (typeof ref !== 'string' || ref.length === 0 || seen.has(ref)) continue;
    seen.add(ref);
    const position = index.get(ref);
    out.push(
      position === undefined
        ? {
            ref,
            label: UNRESOLVED_CITATION,
            description:
              'The retrieval behind this is not in the record shown below. Treat the claim as unverified.',
            resolved: false,
          }
        : {
            ref,
            label: String(position),
            description: `Based on record ${position} under “Based on Phibian records”.`,
            resolved: true,
          },
    );
  }
  return out;
}

/**
 * §24 SUPPORTING EVIDENCE — what the explanation as a WHOLE rests on.
 *
 * THREE ANSWERS, and the middle one is why this is a function rather than an
 * `if (length)` at the call site:
 *
 *   `no_manifest` — the response carried no retrievals at all. There is nothing
 *     to cite and nothing to say; the grounding caution above the panel already
 *     tells this reader their answer was not checked against their records, and
 *     repeating it here in the provenance slot is the sentence L182 removed
 *     from the evidence panel for reading as a fault report.
 *   `uncited`     — retrievals happened and the explanation named none of them
 *     at envelope level. That is a real and reportable fact about the
 *     reasoning: the claims may still carry their own refs, but nothing was
 *     offered as the shared basis.
 *   `cited`       — the citations, in the server's order.
 */
export type SupportingEvidenceView =
  | { readonly kind: 'no_manifest' }
  | { readonly kind: 'uncited' }
  | { readonly kind: 'cited'; readonly citations: readonly Citation[] };

export function supportingEvidenceView(
  refs: readonly string[] | undefined,
  index: ReadonlyMap<string, number>,
  manifestSize: number,
): SupportingEvidenceView {
  if (manifestSize === 0) return { kind: 'no_manifest' };
  const citations = citationsFor(refs, index);
  if (citations.length === 0) return { kind: 'uncited' };
  return { kind: 'cited', citations };
}

/** The mechanism vocabulary, in the reader's words rather than the enum's. */
const MECHANISM_TEXT: Readonly<Record<intelligence.ExplanationMechanism, string>> = {
  documented_rule: 'a written rule',
  threshold_crossing: 'a threshold being crossed',
  scheduled_delta: 'a planned change',
  process_failure: 'something upstream failing',
  intervention_effect: 'something somebody did',
};

export interface FactorView {
  readonly label: string;
  readonly statement: string;
  /** The heading that keeps a cause, a coincidence and a guess apart. */
  readonly basisLabel: string;
  /** The sentence under it, saying what the basis is worth. */
  readonly basisNote: string;
  /** `true` only for `model_hypothesis` — the one with nothing behind it. */
  readonly unevidenced: boolean;
  /**
   * The retrieval this factor rests on, or undefined for the guess.
   *
   * Carried so the panel can put a citation marker beside the factor. It is
   * `undefined` for exactly `model_hypothesis` and never for the other two —
   * the wire union gives `evidenceRef` to `mechanism` and `correlation` alone —
   * so a factor with no marker is a factor the server SAID had nothing behind
   * it, which is the same discriminant `unevidenced` carries, made visible in
   * the one place a reader looks for a source.
   */
  readonly evidenceRef?: string;
}

/**
 * One factor, with the discriminant preserved.
 *
 * `basis` is classified BY THE SERVER and it is the only thing separating a
 * cause from a coincidence from a guess. A surface that renders all three as
 * bullets in one list has undone the classification — the reader gets three
 * equally-weighted reasons, one of which the system explicitly said it had no
 * evidence for.
 */
export function factorView(factor: ExplanationFactor): FactorView {
  if (factor.basis === 'mechanism') {
    return {
      label: factor.label,
      statement: factor.statement,
      basisLabel: 'Cause',
      basisNote: `Linked to the change by ${MECHANISM_TEXT[factor.mechanism] ?? 'a recorded mechanism'}, in your records.`,
      unevidenced: false,
      evidenceRef: factor.evidenceRef,
    };
  }
  if (factor.basis === 'correlation') {
    return {
      label: factor.label,
      statement: factor.statement,
      basisLabel: 'Moved with it',
      basisNote:
        'This is in your records and it moved alongside the change. Nothing links the two — it may be a cause, and it may be a coincidence.',
      unevidenced: false,
      evidenceRef: factor.evidenceRef,
    };
  }
  return {
    label: factor.label,
    statement: factor.statement,
    basisLabel: 'Phibian’s guess',
    basisNote:
      'Nothing in your records stands behind this. Phibian is suggesting it, not reporting it.',
    unevidenced: true,
  };
}

// ---------------------------------------------------------------------------
// §24 DATA FRESHNESS — six answers, none of which is "now"
// ---------------------------------------------------------------------------

type QualificationState = ExplanationTemporal['qualification']['state'];

export interface TemporalView {
  readonly text: string;
  /** 'warn' for data that has aged out of usefulness; 'note' otherwise. */
  readonly tone: 'warn' | 'note';
  /** When the ANSWER was composed. Never labelled "as of". */
  readonly composedAtMs: number;
}

/**
 * The one qualification to put on the answer's currency.
 *
 * SIX ANSWERS. `unknown` is NOT "now" and must never render as current — the
 * source was either never asked or asked and unable to say. `not_applicable`
 * is a catalog read, where a staleness note would be a warning about a risk
 * that does not exist. `no_evidence` is a sixth answer again: an explanation
 * that cited nothing time-bearing has no freshness to report, which is a
 * different statement from one whose every citation was a catalog.
 *
 * And `composedAtMs` is carried separately and named separately, because it is
 * when a model ran, not when anything was true.
 */
const QUALIFICATION_TEXT: Readonly<
  Record<QualificationState, { text: string; tone: 'warn' | 'note' }>
> = {
  fresh: {
    tone: 'note',
    text: 'The records behind this were current when Phibian read them.',
  },
  stale: {
    tone: 'warn',
    text: 'Some of the records behind this had not been refreshed recently when Phibian read them, so this may describe a situation that has since moved.',
  },
  expired: {
    tone: 'warn',
    text: 'Some of the records behind this were past the point where they can be relied on as current. Treat this as a description of the past, not of now.',
  },
  unknown: {
    tone: 'warn',
    text: 'How current the records behind this are was not reported — the source either wasn’t asked or couldn’t say. That is not the same as up to date, and it should not be read as “now”.',
  },
  not_applicable: {
    tone: 'note',
    text: 'Nothing this answer rests on goes out of date — it was read from definitions and catalogs, which don’t age.',
  },
  no_evidence: {
    tone: 'note',
    text: 'This reasoning didn’t cite anything that ages, so there is no currency to report on it.',
  },
};

export function temporalView(temporal: ExplanationTemporal | undefined): TemporalView | null {
  if (temporal === undefined) return null;
  const entry = QUALIFICATION_TEXT[temporal.qualification.state];
  if (entry === undefined) {
    return {
      tone: 'warn',
      text: 'How current the records behind this are could not be established.',
      composedAtMs: temporal.composedAtMs,
    };
  }
  return { ...entry, composedAtMs: temporal.composedAtMs };
}

// ---------------------------------------------------------------------------
// §24 OWNER — withheld is not absent
// ---------------------------------------------------------------------------

/**
 * Who is accountable, as much of it as this reader may be told.
 *
 * THE ELEMENT MISSING means nothing canonical names an owner — no single
 * program in scope, or a program with no lead. THE ELEMENT PRESENT WITH
 * `identityWithheld` means there IS one and this viewer may not be told who.
 * "Not shown to you" and "unowned" are opposite statements about the same
 * program and a surface that renders them alike has told half its readers the
 * wrong one.
 *
 * `dated: false` is carried through rather than dropped: the naming relation is
 * undated, so this is who leads the program NOW — not who led it when the data
 * this answer is about was true.
 */
export function ownerSentence(owner: ExplanationOwner | undefined): string | null {
  if (owner === undefined) return null;
  const tense = owner.dated
    ? ''
    : ' This is who leads that program now, which may not be who led it when this was happening.';
  if (owner.identityWithheld) {
    return `Somebody is accountable for this — the program has a lead. You aren’t shown who.${tense}`;
  }
  if (owner.displayName === null || owner.displayName.trim() === '') {
    return 'The program this concerns has no lead recorded, so nobody is named as accountable for it.';
  }
  return `${owner.displayName} leads the program this concerns.${tense}`;
}

// ---------------------------------------------------------------------------
// §24 UNCERTAINTY
// ---------------------------------------------------------------------------

const UNCERTAINTY_LABEL: Readonly<Record<Explanation['uncertainty']['level'], string>> = {
  none: 'Not in doubt',
  low: 'Little doubt',
  medium: 'Some doubt',
  high: 'Considerable doubt',
};

export function uncertaintyLabel(level: Explanation['uncertainty']['level']): string {
  return UNCERTAINTY_LABEL[level] ?? level;
}

/**
 * The heading over the causal claim, which is a claim the server checked.
 *
 * `assertsCausation` is not decoration: the composer refuses an envelope that
 * asserts it over factors carrying no typed mechanism link
 * (`causation_without_mechanism`). So a composed explanation that says `true`
 * here has passed that check, and one that says `false` is describing what it
 * observed rather than what it believes caused it. Saying so is cheap and
 * stops a reader importing causation the answer did not claim.
 */
export function causationNote(explanation: Explanation): string {
  return explanation.assertsCausation
    ? 'Phibian is claiming this is what caused the change, and each cause below names what links it to the change.'
    : 'Phibian is describing what it found alongside the change, and is not claiming any of it caused it.';
}
